import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ArrowRight, CreditCard, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { cn } from '../../lib/utils';

// Changement de catégorie d'une équipe qui a PAYÉ son inscription : il passe
// toujours par le flux officiel (paiement du complément, remboursement, ou
// changement direct à prix égal — LOG-101). Ce dialogue montre ce qui va se
// passer avant de le faire.
export interface CategoryChangePreview {
  old_category_name: string;
  new_category_name: string;
  old_price_cents: number;
  new_price_cents: number;
  difference_cents: number;
  spots_remaining: number | null;
  team_size_match: boolean;
  old_team_size?: number;
  new_team_size?: number;
  contact_email?: string;
  has_paid_registration?: boolean;
}

interface Props {
  preview: CategoryChangePreview | null;
  loading: boolean;
  error: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export function CategoryChangeDialog({ preview, loading, error, onConfirm, onCancel }: Props) {
  const { t } = useTranslation();
  const euros = (c: number) => (c / 100).toFixed(2);
  return (
    <Dialog open={!!preview} onOpenChange={(open) => { if (!open && !loading) onCancel(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t('teams.categoryChange.title')}
          </DialogTitle>
        </DialogHeader>

        {preview && (
          <div className="space-y-4 py-2">
            <div className="flex items-center gap-3 rounded-lg bg-muted/50 p-3">
              <div className="flex-1 text-center">
                <div className="mb-1 text-xs text-muted-foreground">{t('teams.categoryChange.from')}</div>
                <div className="font-medium">{preview.old_category_name}</div>
                <div className="text-sm text-muted-foreground">{euros(preview.old_price_cents)}€</div>
              </div>
              <ArrowRight size={20} className="shrink-0 text-muted-foreground" />
              <div className="flex-1 text-center">
                <div className="mb-1 text-xs text-muted-foreground">{t('teams.categoryChange.to')}</div>
                <div className="font-medium">{preview.new_category_name}</div>
                <div className="text-sm text-muted-foreground">{euros(preview.new_price_cents)}€</div>
              </div>
            </div>

            <div className="flex items-center justify-between rounded-lg border p-3">
              <span className="text-sm font-medium">{t('teams.categoryChange.priceDifference')}</span>
              <span className={cn(
                'text-lg font-bold',
                preview.difference_cents > 0 ? 'text-warning' : preview.difference_cents < 0 ? 'text-success' : 'text-muted-foreground',
              )}>
                {preview.difference_cents > 0 ? '+' : ''}{euros(preview.difference_cents)}€
              </span>
            </div>

            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{t('teams.categoryChange.spotsRemaining')}</span>
              <Badge variant={preview.spots_remaining === null || preview.spots_remaining > 0 ? 'outline' : 'destructive'}>
                {preview.spots_remaining !== null ? preview.spots_remaining : t('teams.categoryChange.unlimited')}
              </Badge>
            </div>

            {!preview.team_size_match && (
              <Alert variant="destructive">
                <AlertTriangle size={16} />
                <AlertDescription>
                  {t('teams.categoryChange.teamSizeMismatch', { current: preview.old_team_size, new: preview.new_team_size })}
                </AlertDescription>
              </Alert>
            )}

            {preview.difference_cents > 0 && (
              <Alert>
                <CreditCard size={16} />
                <AlertDescription>
                  {t('teams.categoryChange.emailWillBeSent', { email: preview.contact_email || '', amount: euros(preview.difference_cents) })}
                </AlertDescription>
              </Alert>
            )}
            {preview.difference_cents < 0 && (
              <Alert>
                <RefreshCw size={16} />
                <AlertDescription>
                  {t('teams.categoryChange.refundWillBeProcessed', { amount: euros(Math.abs(preview.difference_cents)) })}
                </AlertDescription>
              </Alert>
            )}
            {preview.difference_cents === 0 && (
              <Alert>
                <RefreshCw size={16} />
                <AlertDescription>{t('teams.categoryChange.samePriceDirectChange')}</AlertDescription>
              </Alert>
            )}
            {preview.difference_cents <= 0 && (
              <p className="text-xs text-muted-foreground">
                {t('teams.categoryChange.participantNotified', { email: preview.contact_email || '' })}
              </p>
            )}

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={loading}>{t('common.cancel')}</Button>
          <Button onClick={onConfirm} disabled={loading || (!!preview && !preview.team_size_match)}>
            {loading && <Loader2 size={16} className="mr-2 animate-spin" />}
            {t('teams.categoryChange.confirmChange')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
